"use client";

import { c as _c } from "react/compiler-runtime";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Route } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getPostLoginDestinationAction } from "@/app/_actions/auth";
import { getOrganizationMembershipsAction } from "@/app/_actions/organizations";
import { signInSchema } from "@/app/auth/login/_lib";
import { clearLastSignInMethod, type LastSignInMethod, readLastSignInMethod, saveLastSignInMethod } from "@/app/auth/login/_lib/last-sign-in-method";
import Logo from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { buildAuthContinuePath } from "@/lib/auth/callback-url";
import { authClient } from "@/lib/auth-client";
import { INDIAN_PHONE_DIGITS, normalizeIndianPhone, sanitizeIndianPhoneInput } from "@/lib/shared/phone";
interface SignInFormProps {
  callbackUrl?: string | null;
}
const LAST_SIGN_IN_METHOD_STORAGE_KEY = "clutch.web.auth.lastSignInMethod";
export default function SignInForm(t0) {
  const $ = _c(73);
  const {
    callbackUrl
  } = t0;
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    isPending: isSessionPending
  } = authClient.useSession();
  const [step, setStep] = useState("phone");
  const [sentTo, setSentTo] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [lastSignInMethod, setLastSignInMethod] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const showDevOtp = process.env.NEXT_PUBLIC_SHOW_OTP === "true";
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      try {
        setLastSignInMethod(readLastSignInMethod(window.localStorage, LAST_SIGN_IN_METHOD_STORAGE_KEY));
      } catch {
        setLastSignInMethod(null);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let handleGoogleSignIn;
  let rememberLastSignInMethod;
  if ($[2] !== callbackUrl || $[3] !== lastSignInMethod) {
    handleGoogleSignIn = async () => {
      setIsGoogleSubmitting(true);
      const previousMethod = lastSignInMethod;
      rememberLastSignInMethod("google");
      ;
      try {
        await authClient.signIn.social({
          provider: "google",
          callbackURL: buildAuthContinuePath(callbackUrl)
        });
      } catch (t3) {
        const error = t3;
        restoreLastSignInMethod(previousMethod);
        setIsGoogleSubmitting(false);
        toast.error(error instanceof Error ? error.message : "Could not start Google sign-in");
      }
    };
    rememberLastSignInMethod = method => {
      try {
        const savedMethod = saveLastSignInMethod(window.localStorage, LAST_SIGN_IN_METHOD_STORAGE_KEY, method);
        setLastSignInMethod(savedMethod);
      } catch {}
    };
    const restoreLastSignInMethod = method_0 => {
      try {
        if (method_0) {
          saveLastSignInMethod(window.localStorage, LAST_SIGN_IN_METHOD_STORAGE_KEY, method_0);
        } else {
          clearLastSignInMethod(window.localStorage, LAST_SIGN_IN_METHOD_STORAGE_KEY);
        }
      } catch {}
      setLastSignInMethod(method_0);
    };
    $[2] = callbackUrl;
    $[3] = lastSignInMethod;
    $[4] = handleGoogleSignIn;
    $[5] = rememberLastSignInMethod;
  } else {
    handleGoogleSignIn = $[4];
    rememberLastSignInMethod = $[5];
  }
  const sendOtpMutation = useMutation({
    mutationFn: _temp,
    onSuccess: (_, phoneNumber_0) => {
      const isResend = step === "otp" && sentTo === phoneNumber_0;
      setStep("otp");
      setSentTo(phoneNumber_0);
      form.setFieldValue("code", "");
      toast.success(isResend ? "A new OTP is on the way" : "OTP sent to your phone number");
      if (showDevOtp) {
        const match = document.cookie.match(/(?:^|;\s*)__dev_otp=(\d{6})/);
        setDevOtp(match?.[1] ?? null);
      }
    },
    onError: _temp2
  });
  let t3;
  if ($[6] !== callbackUrl || $[7] !== queryClient) {
    t3 = async t4 => {
      const {
        phoneNumber: phoneNumber_1,
        code
      } = t4;
      const {
        error: error_2,
        data: data_0
      } = await authClient.phoneNumber.verify({
        phoneNumber: phoneNumber_1,
        code,
        disableSession: false,
        updatePhoneNumber: false
      });
      if (error_2) {
        throw new Error(error_2.message || error_2.statusText);
      }
      setIsTransitioning(true);
      try {
        await queryClient.fetchQuery({
          queryKey: ["organization-memberships"],
          queryFn: _temp3
        });
      } catch {}
      const destination = await getPostLoginDestinationAction(callbackUrl);
      return {
        data: data_0,
        destination
      };
    };
    $[6] = callbackUrl;
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== rememberLastSignInMethod || $[10] !== router) {
    t4 = t5 => {
      const {
        destination: destination_0
      } = t5;
      rememberLastSignInMethod("phone");
      toast.success("Sign in successful");
      router.replace(destination_0 as Route);
    };
    $[9] = rememberLastSignInMethod;
    $[10] = router;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: _temp4
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const verifyOtpMutation = useMutation(t5);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      phoneNumber: "",
      code: ""
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== sendOtpMutation || $[17] !== step || $[18] !== verifyOtpMutation) {
    t7 = async t8 => {
      const {
        value
      } = t8;
      const phoneNumber_2 = normalizeIndianPhone(value.phoneNumber);
      if (!phoneNumber_2) {
        toast.error("Enter a valid Indian mobile number");
        return;
      }
      if (step === "phone") {
        sendOtpMutation.mutate(phoneNumber_2);
        return;
      }
      const code_0 = value.code.trim();
      if (!/^\d{6}$/.test(code_0)) {
        toast.error("Enter the 6 digit OTP");
        return;
      }
      verifyOtpMutation.mutate({
        phoneNumber: phoneNumber_2,
        code: code_0
      });
    };
    $[16] = sendOtpMutation;
    $[17] = step;
    $[18] = verifyOtpMutation;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      onSubmit: signInSchema
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7) {
    t9 = {
      defaultValues: t6,
      onSubmit: t7,
      validators: t8
    };
    $[21] = t7;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const form = useForm(t9);
  if (isTransitioning) {
    let t10;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="w-full max-w-100 rounded-3xl px-6 py-10 text-center backdrop-blur"><div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10"><Spinner className="size-5" /></div><h1 className="font-semibold text-2xl tracking-tight">Signing you in</h1><p className="mt-3 text-muted-foreground text-sm">We're securing your session and taking you to the right place.</p></div>;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  if (isSessionPending) {
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Spinner className="size-6" />;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    return t10;
  }
  let t10;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mx-auto mb-6 flex items-center justify-center"><Link href="/" aria-label="Go to homepage" className="inline-flex items-center justify-center"><Logo variant="logo" size="xl" className="text-3xl" /></Link></div>;
    t11 = <h1 className="font-semibold text-2xl tracking-tight">Welcome to Clutch</h1>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  let t12;
  if ($[27] !== form || $[28] !== sentTo || $[29] !== step) {
    t12 = step === "phone" ? <p className="text-pretty text-muted-foreground text-sm">Enter your phone number to sign in or create a new account.</p> : <p className="flex items-center justify-center gap-1.5 text-muted-foreground text-sm">We sent a 6 digit code to {sentTo}<button type="button" onClick={() => {
        setStep("phone");
        form.setFieldValue("code", "");
      }} className="font-medium text-primary hover:underline">Change</button></p>;
    $[27] = form;
    $[28] = sentTo;
    $[29] = step;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== devOtp || $[32] !== step) {
    t13 = showDevOtp && devOtp && step === "otp" ? <div className="mt-2 rounded-lg border border-amber-500/50 border-dashed bg-amber-500/10 px-3 py-2 text-center"><p className="font-medium text-amber-600 text-xs dark:text-amber-400">DEV OTP:{" "}<span className="font-mono text-sm tracking-widest">{devOtp}</span></p></div> : null;
    $[31] = devOtp;
    $[32] = step;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13) {
    t14 = <div className="flex flex-col space-y-2 text-center">{t10}{t11}{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== form) {
    t15 = e => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    };
    $[37] = form;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== form.Field || $[40] !== lastSignInMethod || $[41] !== step) {
    t16 = step === "phone" ? <div className="grid gap-2"><form.Field name="phoneNumber">{field => <div className="grid gap-6"><Label className="sr-only" htmlFor={field.name}>Phone Number</Label><div className="relative"><Input id={field.name} name={field.name} type="tel" className="h-12 rounded-xl border-border bg-muted/50 text-center font-medium text-lg tracking-wide transition-colors hover:bg-muted/80 focus:bg-background dark:border-transparent" inputMode="numeric" autoComplete="tel-national" maxLength={INDIAN_PHONE_DIGITS} placeholder="9876543210" value={field.state.value} onBlur={field.handleBlur} onChange={e_0 => {
              field.handleChange(sanitizeIndianPhoneInput(e_0.target.value));
            }} />{lastSignInMethod === "phone" ? <span className="pointer-events-none absolute -top-2 -right-3 rotate-8 rounded-full bg-cadre-teal px-2 py-0.5 font-semibold text-[12px] text-white leading-4 shadow-sm">Last used</span> : null}</div>{field.state.meta.errors.slice(0, 1).map(_temp5)}</div>}</form.Field></div> : null;
    $[39] = form.Field;
    $[40] = lastSignInMethod;
    $[41] = step;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== form.Field || $[44] !== step) {
    t17 = step === "otp" ? <div className="grid gap-2"><Label className="sr-only" htmlFor="otp-code">OTP Code</Label><form.Field name="code">{_temp6}</form.Field></div> : null;
    $[43] = form.Field;
    $[44] = step;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== form || $[47] !== sendOtpMutation || $[48] !== step || $[49] !== verifyOtpMutation) {
    t18 = state => {
      const isSubmitting = state.isSubmitting || sendOtpMutation.isPending || verifyOtpMutation.isPending;
      return <div className="grid gap-4"><Button type="submit" className="h-12 w-full rounded-xl font-medium text-base" disabled={!state.canSubmit || isSubmitting}>{isSubmitting ? <Spinner className="size-4" /> : step === "phone" ? "Continue" : "Verify"}</Button>{step === "otp" ? <Button type="button" variant="ghost" className="h-12 w-full rounded-xl text-muted-foreground text-sm hover:cursor-pointer hover:bg-transparent hover:text-primary hover:underline" disabled={sendOtpMutation.isPending} onClick={() => {
          const phoneNumber_3 = normalizeIndianPhone(form.getFieldValue("phoneNumber"));
          if (!phoneNumber_3) {
            toast.error("Enter a valid Indian mobile number");
            return;
          }
          sendOtpMutation.mutate(phoneNumber_3);
        }}>{sendOtpMutation.isPending ? <Spinner className="size-4" /> : "Didn't receive a code? Resend"}</Button> : null}</div>;
    };
    $[46] = form;
    $[47] = sendOtpMutation;
    $[48] = step;
    $[49] = verifyOtpMutation;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== form.Subscribe || $[52] !== t18) {
    t19 = <form.Subscribe>{t18}</form.Subscribe>;
    $[51] = form.Subscribe;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t15 || $[55] !== t16 || $[56] !== t17 || $[57] !== t19) {
    t20 = <form onSubmit={t15} className="space-y-6">{t16}{t17}{t19}</form>;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== handleGoogleSignIn || $[60] !== isGoogleSubmitting || $[61] !== lastSignInMethod || $[62] !== step) {
    t21 = step === "phone" ? <><div className="flex items-center gap-3 text-muted-foreground/60 text-xs uppercase tracking-wider"><span className="h-px flex-1 bg-border" /><span>or</span><span className="h-px flex-1 bg-border" /></div><Button type="button" variant="outline" onClick={handleGoogleSignIn} disabled={isGoogleSubmitting} className="relative h-12 w-full overflow-visible rounded-xl font-medium text-base">{isGoogleSubmitting ? <Spinner className="size-4" /> : <><svg aria-hidden="true" viewBox="0 0 48 48" className="size-6"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" /><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" /><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" /><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" /></svg>Continue with Google{lastSignInMethod === "google" ? <span className="pointer-events-none absolute -top-2 -right-3 rotate-8 rounded-full bg-cadre-teal px-2 py-0.5 font-semibold text-[12px] text-white leading-4 shadow-sm">Last used</span> : null}</>}</Button></> : null;
    $[59] = handleGoogleSignIn;
    $[60] = isGoogleSubmitting;
    $[61] = lastSignInMethod;
    $[62] = step;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t20 || $[65] !== t21) {
    t22 = <div className="mb-4 grid gap-4">{t20}{t21}</div>;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== step) {
    t23 = step === "phone" ? <p className="px-6 text-center text-muted-foreground/60 text-sm leading-6">By continuing, you acknowledge that you agree to the{" "}<Link href="/terms" className="font-medium text-muted-foreground/80 underline underline-offset-4 transition-colors hover:text-muted-foreground">Terms of Service</Link>{" "}and{" "}<Link href="/privacy" className="font-medium text-muted-foreground/80 underline underline-offset-4 transition-colors hover:text-muted-foreground">Privacy Policy</Link>.</p> : null;
    $[67] = step;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t14 || $[70] !== t22 || $[71] !== t23) {
    t24 = <div className="flex w-full max-w-100 flex-col justify-center space-y-8">{t14}{t22}{t23}</div>;
    $[69] = t14;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
}
function _temp6(field_0) {
  return <InputOTP id="otp-code" maxLength={6} value={field_0.state.value} onChange={value_0 => field_0.handleChange(value_0)} pattern="^[0-9]+$" containerClassName="justify-center" autoFocus={true}><InputOTPGroup className="gap-2"><InputOTPSlot index={0} className="size-12 rounded-xl border-transparent bg-muted/50 text-lg transition-colors hover:border-border sm:size-14 sm:text-xl" /><InputOTPSlot index={1} className="size-12 rounded-xl border-transparent bg-muted/50 text-lg transition-colors hover:border-border sm:size-14 sm:text-xl" /><InputOTPSlot index={2} className="size-12 rounded-xl border-transparent bg-muted/50 text-lg transition-colors hover:border-border sm:size-14 sm:text-xl" /></InputOTPGroup><InputOTPSeparator className="mx-1 text-muted-foreground" /><InputOTPGroup className="gap-2"><InputOTPSlot index={3} className="size-12 rounded-xl border-transparent bg-muted/50 text-lg transition-colors hover:border-border sm:size-14 sm:text-xl" /><InputOTPSlot index={4} className="size-12 rounded-xl border-transparent bg-muted/50 text-lg transition-colors hover:border-border sm:size-14 sm:text-xl" /><InputOTPSlot index={5} className="size-12 rounded-xl border-transparent bg-muted/50 text-lg transition-colors hover:border-border sm:size-14 sm:text-xl" /></InputOTPGroup></InputOTP>;
}
function _temp5(error_4) {
  return <p key={error_4?.message} className="fade-in slide-in-from-top-1 animate-in text-center text-destructive text-sm transition-all duration-300">{error_4?.message}</p>;
}
function _temp4(error_3) {
  toast.error(error_3.message);
}
function _temp3() {
  return getOrganizationMembershipsAction();
}
function _temp2(error_1) {
  toast.error(error_1.message);
}
async function _temp(phoneNumber) {
  const {
    error: error_0,
    data
  } = await authClient.phoneNumber.sendOtp({
    phoneNumber
  });
  if (error_0) {
    throw new Error(error_0.message || error_0.statusText);
  }
  return data;
}